import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  CircularProgress,
  Alert,
  AlertTitle
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import 'moment/locale/fr';

import { useTranslation } from 'react-i18next';
// Import des statuts de disponibilité
import { AVAILABILITY_STATUS, AVAILABILITY_DESCRIPTIONS } from '../../constants/availabilityStatus';
import {
  availabilityRangesOverlap,
  getInclusiveAvailabilityEnd,
  toExclusiveAvailabilityRange
} from '../../utils/dateUtils';

const getInitialStartDate = (initialData) => (
  initialData?.available_from ? moment(initialData.available_from)
    : initialData?.startDate ? moment(initialData.startDate)
    : moment()
);

const getInitialEndDate = (initialData) => {
  if (initialData?.available_to) {
    const displayEnd = getInclusiveAvailabilityEnd(initialData.available_to) || initialData.available_to;
    return moment(displayEnd);
  }

  if (initialData?.endDate) {
    return moment(initialData.endDate);
  }

  return moment().add(7, 'days');
};

/**
 * Composant modal pour éditer ou créer une période de disponibilité
 */
const PeriodFormModal = ({ 
  open, 
  onClose, 
  onSave, 
  initialData = {}, 
  isSubmitting = false,
  error = null,
  mode = 'add', // 'add', 'edit', 'block', 'free'
  availabilityPeriods = [] // Liste des périodes existantes pour validation
}) => {
  const { t } = useTranslation();
  // Configuration des titres selon le mode
  const titles = {
    'add': t('periodFormModal:title.add', 'Ajouter une période'),
    'edit': t('periodFormModal:title.edit', 'Modifier la période'),
    'block': t('periodFormModal:title.block', 'Bloquer une période'),
    'free': t('periodFormModal:title.free', 'Libérer une période')
  };

  // Initialisation de l'état du formulaire
  const [formData, setFormData] = useState({
    startDate: getInitialStartDate(initialData),
    endDate: getInitialEndDate(initialData),
    status: initialData?.status || 
            (mode === 'block' ? AVAILABILITY_STATUS.BLOCKED : 
             mode === 'free' ? AVAILABILITY_STATUS.AVAILABLE : 
             AVAILABILITY_STATUS.AVAILABLE),
    reason: initialData?.reason || ''
  });

  // Erreurs de validation
  const [validationErrors, setValidationErrors] = useState({});

  // Mettre à jour le formulaire quand initialData change
  useEffect(() => {
    if (initialData) {
      setFormData({
        startDate: getInitialStartDate(initialData),
        endDate: getInitialEndDate(initialData),
        status: initialData.status || 
                (mode === 'block' ? AVAILABILITY_STATUS.BLOCKED : 
                 mode === 'free' ? AVAILABILITY_STATUS.AVAILABLE : 
                 AVAILABILITY_STATUS.AVAILABLE),
        reason: initialData.reason || ''
      });
    }
  }, [initialData, mode]);

  // Purger les erreurs de validation à chaque ouverture du modal
  useEffect(() => {
    if (open) {
      setValidationErrors({});
    }
  }, [open]);

  // Gestionnaire de changement des champs du formulaire
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Effacer l'erreur de validation pour ce champ
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Validation des chevauchements
  const validateOverlaps = () => {
    const currentRange = toExclusiveAvailabilityRange(formData.startDate, formData.endDate);
    const currentPeriodId = initialData?.id;
    
    for (const period of availabilityPeriods) {
      if (currentPeriodId && period.id === currentPeriodId) continue;

      const periodStart = period.available_from || period.start_date;
      const periodEnd = period.available_to || period.end_date;

      if (availabilityRangesOverlap(
        currentRange.available_from,
        currentRange.available_to,
        periodStart,
        periodEnd
      )) {
        const displayEnd = getInclusiveAvailabilityEnd(periodEnd) || periodEnd;
        return t('periodFormModal:validation.overlap', {
          start: moment(periodStart).format('DD/MM/YYYY'),
          end: moment(displayEnd).format('DD/MM/YYYY'),
          defaultValue: `Cette période chevauche avec une période existante (${moment(periodStart).format('DD/MM/YYYY')} - ${moment(displayEnd).format('DD/MM/YYYY')})`
        });
      }
    }
    return null;
  };

  // Validation du formulaire
  const validateForm = () => {
    const errors = {};
    
    if (!formData.startDate || !moment(formData.startDate).isValid()) {
      errors.startDate = t('periodFormModal:validation.startDate', 'Date de début invalide');
    }
    
    if (!formData.endDate || !moment(formData.endDate).isValid()) {
      errors.endDate = t('periodFormModal:validation.endDate', 'Date de fin invalide');
    }
    
    if (moment(formData.endDate).isBefore(moment(formData.startDate))) {
      errors.endDate = t('periodFormModal:validation.endDateAfterStart', 'La date de fin doit être après la date de début');
    }
    
    if (!formData.status) {
      errors.status = t('periodFormModal:validation.status', 'Veuillez sélectionner un statut');
    }
    
    // Vérifier les chevauchements
    const overlapError = validateOverlaps();
    if (overlapError) {
      errors.overlap = overlapError;
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Soumission du formulaire
  const handleSubmit = () => {
    if (!validateForm()) {
      console.log('Erreurs de validation détectées:', validationErrors);
      return;
    }

    const exclusiveRange = toExclusiveAvailabilityRange(formData.startDate, formData.endDate);
    
    console.log('Soumission validée, données envoyées:', formData);
    onSave({
      id: initialData?.id,
      available_from: exclusiveRange.available_from,
      available_to: exclusiveRange.available_to,
      startDate: exclusiveRange.startDate,
      endDate: exclusiveRange.endDate,
      status: formData.status,
      reason: formData.reason || null
    });
  };

  // Liste des statuts disponibles selon le mode
  const getAvailableStatuses = () => {
    if (mode === 'block') return [AVAILABILITY_STATUS.BLOCKED];
    if (mode === 'free') return [AVAILABILITY_STATUS.AVAILABLE];
    
    // Pour le mode 'add' ou 'edit', on montre tous les statuts
    return Object.keys(AVAILABILITY_STATUS).map(key => AVAILABILITY_STATUS[key]);
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{ sx: { p: 1 } }}
    >
      <DialogTitle>{titles[mode] || t('periodFormModal:title.default', 'Gérer une période')}</DialogTitle>
      
      <DialogContent>
        <Box sx={{ my: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('periodFormModal:error.global', error)}
            </Alert>
          )}

          <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale="fr">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label={t('periodFormModal:label.startDate', 'Date de début')}
                  value={formData.startDate}
                  onChange={(newValue) => handleChange('startDate', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={!!validationErrors.startDate}
                      helperText={validationErrors.startDate}
                      margin="dense"
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label={t('periodFormModal:label.endDate', 'Date de fin')}
                  value={formData.endDate}
                  onChange={(newValue) => handleChange('endDate', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={!!validationErrors.endDate}
                      helperText={validationErrors.endDate}
                      margin="dense"
                    />
                  )}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl 
                  fullWidth 
                  margin="dense"
                  error={!!validationErrors.status}
                  disabled={mode === 'block' || mode === 'free'}
                >
                  <InputLabel>{t('periodFormModal:label.status', 'Statut')}</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                    label={t('periodFormModal:label.status', 'Statut')}
                  >
                    {getAvailableStatuses().map(status => {
                      const description = AVAILABILITY_DESCRIPTIONS[status] || { fr: status, color: '#666' };
                      return (
                        <MenuItem 
                          key={status} 
                          value={status}
                          sx={{ 
                            borderLeft: `4px solid ${description.color}`,
                            pl: 2
                          }}
                        >
                          {t(`availabilityStatus.${status}`, description.fr)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {validationErrors.status && (
                    <FormHelperText>{validationErrors.status}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              
              {/* Affichage des erreurs de validation */}
              {Object.keys(validationErrors).length > 0 && (
                <Grid item xs={12}>
                  <Alert severity="error">
                    <AlertTitle>{t('periodFormModal:validation.title', 'Erreurs de validation')}</AlertTitle>
                    {Object.entries(validationErrors).map(([key, error]) => (
                      <div key={key}>{error}</div>
                    ))}
                  </Alert>
                </Grid>
              )}
              
              <Grid item xs={12}>
                <TextField
                  label={t('periodFormModal:label.reason', 'Raison (optionnel)')}
                  fullWidth
                  margin="dense"
                  value={formData.reason}
                  onChange={(e) => handleChange('reason', e.target.value)}
                  multiline
                  rows={3}
                  placeholder={t('periodFormModal:placeholder.reason', 'Exemple: Maintenance prévue, Vacances personnelles, etc.')}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          {t('periodFormModal:button.cancel', 'Annuler')}
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary" 
          disabled={isSubmitting}
          startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
        >
          {isSubmitting ? t('periodFormModal:button.saving', 'Enregistrement...') : t('periodFormModal:button.save', 'Enregistrer')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PeriodFormModal;
